import React, { useMemo, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  IoDocumentText,
  IoTime,
  IoCheckmarkCircle,
  IoCalendar,
  IoAdd,
  IoBarChart,
  IoTrendingUp,
  IoSchoolOutline,
} from 'react-icons/io5';
import { User } from '@/types';
import { API } from '@/utils/api.utils';
import api from '@/utils/api.utils';
import './Progress.css';

interface University {
  university_id: number;
  user_id: number;
  status: string;
  is_suggested: boolean;
  id: number;
  university_name: string;
}

interface UniversityResponse {
  IN_PROGRESS: {
    universities: University[];
    count: number;
  };
  PENDING: {
    universities: University[];
    count: number;
  };
  COMPLETED: {
    universities: University[];
    count: number;
  };
}

interface ApplicationProgress {
  id: number;
  name: string;
  deadline: string;
  status: string;
  completion: number;
  nextSteps?: string[];
}

interface ProgressProps {
  user?: User | null;
}

const Progress: React.FC<ProgressProps> = ({ user }) => {
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Helper function to get next steps - defined before use
  const getNextSteps = useCallback((universityName: string, status: string): string[] => {
    if (status === 'COMPLETED') {
      return [
        'Follow up with admissions office',
        'Prepare for potential interview',
        'Continue demonstrating interest',
        'Complete scholarship applications',
      ];
    }

    if (status === 'IN_PROGRESS') {
      return [
        'Attend at least 1 demonstrated interest opportunity',
        'Finish your 2 remaining supplemental essays',
        'Email your admissions rep',
        'Apply for financial aid before Dec 1',
      ];
    }

    return [
      'Start your application essays',
      'Request letters of recommendation',
      'Schedule campus visit or virtual session',
      'Research scholarship opportunities',
    ];
  }, []);

  // Fetch user universities
  const { data: universitiesData, isLoading } = useQuery<UniversityResponse>({
    queryKey: ['user-universities'],
    queryFn: async () => {
      const response = await api.get(API.USER_UNIVERSITIES);
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  // Transform universities to application progress format
  const applicationProgress: ApplicationProgress[] = useMemo(() => {
    if (!universitiesData) return [];

    const allUniversities = [
      ...universitiesData.IN_PROGRESS.universities,
      ...universitiesData.PENDING.universities,
      ...universitiesData.COMPLETED.universities,
    ];

    return allUniversities.map((univ, index) => ({
      id: univ.university_id || index,
      name: univ.university_name,
      deadline: 'Jan 5, 2025',
      status: univ.status,
      completion: univ.status === 'COMPLETED' ? 100 : univ.status === 'IN_PROGRESS' ? 75 : 50,
      nextSteps: getNextSteps(univ.university_name, univ.status),
    }));
  }, [universitiesData, getNextSteps]);

  // Filter applications based on status
  const filteredApplications = useMemo(() => {
    if (filterStatus === 'all') return applicationProgress;
    return applicationProgress.filter(app => {
      switch (filterStatus) {
        case 'in_progress':
          return app.status === 'IN_PROGRESS';
        case 'submitted':
          return app.status === 'COMPLETED';
        case 'pending':
          return app.status === 'PENDING';
        default:
          return true;
      }
    });
  }, [applicationProgress, filterStatus]);

  // Analytics data
  const analyticsData = useMemo(() => {
    const totalApps = applicationProgress.length;
    const completedApps = applicationProgress.filter(app => app.status === 'COMPLETED').length;
    const inProgressApps = applicationProgress.filter(app => app.status === 'IN_PROGRESS').length;
    const avgCompletion = totalApps > 0 ? 
      Math.round(applicationProgress.reduce((acc, app) => acc + app.completion, 0) / totalApps) : 0;

    return {
      totalApplications: totalApps,
      completedApplications: completedApps,
      inProgressApplications: inProgressApps,
      avgCompletionTime: '12 days',
      successRate: `${avgCompletion}%`,
      estimatedDecisions: 'Mar 15',
    };
  }, [applicationProgress]);

  const getStatusBadgeColor = useCallback((status: string): string => {
    switch (status) {
      case 'COMPLETED':
        return 'status-badge-green';
      case 'IN_PROGRESS':
        return 'status-badge-yellow';
      case 'PENDING':
        return 'status-badge-gray';
      default:
        return 'status-badge-gray';
    }
  }, []);

  const getStatusLabel = useCallback((status: string): string => {
    switch (status) {
      case 'COMPLETED':
        return 'Submitted';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'PENDING':
        return 'Pending';
      default:
        return status;
    }
  }, []);

  if (isLoading) {
    return (
      <div className="progress-page">
        <div className="progress-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading your progress...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="progress-page">
      <div className="progress-container">
        {/* Analytics Section */}
        <div className="analytics-section">
          <div className="analytics-card">
            <h2 className="card-title">Application Analytics</h2>

            {/* Charts Container - Placeholder for now */}
            <div className="analytics-charts-container">
              <div className="chart-wrapper">
                <h3 className="chart-title">Monthly Progress Overview</h3>
                <div className="chart-placeholder">
                  <IoTrendingUp className="chart-icon" />
                  <p>Monthly progress chart</p>
                  <div className="chart-mock">
                    <div className="chart-bar" style={{ height: '20%' }}></div>
                    <div className="chart-bar" style={{ height: '40%' }}></div>
                    <div className="chart-bar" style={{ height: '60%' }}></div>
                    <div className="chart-bar" style={{ height: '80%' }}></div>
                    <div className="chart-bar" style={{ height: '100%' }}></div>
                  </div>
                </div>
              </div>

              <div className="chart-wrapper">
                <h3 className="chart-title">Applications by School Type</h3>
                <div className="chart-placeholder">
                  <IoBarChart className="chart-icon" />
                  <p>School type distribution</p>
                  <div className="pie-chart-mock">
                    <div className="pie-slice reach" title="Reach Schools"></div>
                    <div className="pie-slice target" title="Target Schools"></div>
                    <div className="pie-slice safety" title="Safety Schools"></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="analytics-grid">
              <div className="analytics-stat">
                <div className="stat-header">
                  <span className="stat-label">Avg. Completion Time</span>
                  <IoTime className="stat-icon" />
                </div>
                <p className="stat-value">{analyticsData.avgCompletionTime}</p>
                <p className="stat-subtext">Per application</p>
              </div>

              <div className="analytics-stat">
                <div className="stat-header">
                  <span className="stat-label">Success Rate</span>
                  <IoCheckmarkCircle className="stat-icon" />
                </div>
                <p className="stat-value">{analyticsData.successRate}</p>
                <p className="stat-subtext">Profile completeness</p>
              </div>

              <div className="analytics-stat">
                <div className="stat-header">
                  <span className="stat-label">Estimated Decisions</span>
                  <IoCalendar className="stat-icon" />
                </div>
                <p className="stat-value">{analyticsData.estimatedDecisions}</p>
                <p className="stat-subtext">Average decision date</p>
              </div>
            </div>
          </div>
        </div>

        {/* Application Progress */}
        <div className="application-progress-card">
          <div className="card-header">
            <h2 className="card-title">Application Progress</h2>
            <select 
              className="progress-filter"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Applications</option>
              <option value="in_progress">In Progress</option>
              <option value="submitted">Submitted</option>
              <option value="pending">Under Review</option>
            </select>
          </div>

          <div className="applications-list">
            {filteredApplications.length > 0 ? (
              filteredApplications.map((app) => (
                <div key={app.id} className="application-item">
                  <div className="application-header">
                    <div className="application-info">
                      <div className="application-icon">
                        <IoSchoolOutline className="application-icon-svg" />
                      </div>
                      <div>
                        <h3 className="application-name">{app.name}</h3>
                        <p className="application-deadline">Regular Decision • Deadline: {app.deadline}</p>
                      </div>
                    </div>
                    <span className={`status-badge ${getStatusBadgeColor(app.status)}`}>
                      {getStatusLabel(app.status)}
                    </span>
                  </div>
                  
                  <div className="application-progress-bar">
                    <div className="progress-bar-bg">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${app.completion}%` }}
                      ></div>
                    </div>
                    <span className="progress-percentage">{app.completion}%</span>
                  </div>
                  
                  <div className="application-status-icons">
                    <span className="status-icon-item">
                      <IoCheckmarkCircle className="status-icon-check" />
                      Common App Complete
                    </span>
                    <span className="status-icon-item">
                      <IoCheckmarkCircle className="status-icon-check" />
                      Essays Submitted
                    </span>
                    <span className="status-icon-item">
                      <IoTime className="status-icon-pending" />
                      Recommendations Pending
                    </span>
                  </div>
                  
                  {/* Recommended Next Steps */}
                  <div className="next-steps-container">
                    <h4 className="next-steps-title">Recommended Next Steps</h4>
                    <ul className="next-steps-list">
                      {app.nextSteps?.map((step, index) => (
                        <li key={index} className="next-step-item">
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                {filterStatus === 'all' ? (
                  <>
                    <IoSchoolOutline className="empty-icon" />
                    <h3>No applications yet</h3>
                    <p>Start by adding a university to your list to track your progress.</p>
                  </>
                ) : (
                  <>
                    <IoDocumentText className="empty-icon" />
                    <h3>No {filterStatus.replace('_', ' ')} applications</h3>
                    <p>Try changing your filter to see more applications.</p>
                  </>
                )}
              </div>
            )}
          </div>

          <button 
            className="add-application-button"
            onClick={() => window.location.href = '/college-list-builder'}
          >
            <IoAdd className="add-icon" />
            <span>Add New Application</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Progress;